import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import OptimizedImage from '../components/OptimizedImage';

const ShareStatsCard = ({ purchaseData, theme, userPersonality, cardType = "purchase" }) => {
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [shareStep, setShareStep] = useState("initial"); // 'initial', 'generated', 'instructions'
  const [generatedImageUrl, setGeneratedImageUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };
  
  // Function to generate emoji representation based on card type and data
  const generateEmojiString = () => {
    // Base emoji string elements
    const baseEmojis = "🛍️ 📊 ";
    
    // Add guilt score emoji
    const guiltEmoji = purchaseData.guiltScore < 30 ? "🟢" : 
                       purchaseData.guiltScore < 70 ? "🟡" : "🔴";
    
    // Add mood emoji
    const moodEmoji = purchaseData.mood === "sad" ? "😢" : 
                     purchaseData.mood === "happy" ? "😊" : 
                     purchaseData.mood === "bored" ? "😑" :
                     purchaseData.mood === "drunk" ? "🍸" :
                     purchaseData.mood === "celebrating" ? "🎉" :
                     purchaseData.mood === "cute" ? "💅" :
                     purchaseData.mood === "fomo" ? "👀" : "🛒";
    
    // Personality emoji based on type
    const personalityEmoji = userPersonality?.includes("Emotional") ? "💸" :
                             userPersonality?.includes("Treat") ? "🤩" :
                             userPersonality?.includes("Mindful") ? "🧠" :
                             userPersonality?.includes("Discount") ? "💯" :
                             userPersonality?.includes("Luxury") ? "💎" : "✨";
    
    // Different formats based on card type
    if (cardType === "purchase") {
      return `${baseEmojis}${guiltEmoji} ${purchaseData.guiltScore}% | ${moodEmoji} | 💰 ${purchaseData.amount} | ${personalityEmoji}`;
    } else if (cardType === "quiz") {
      return `📝 Quiz Result: ${purchaseData.resultEmoji || '🧐'} ${purchaseData.result || 'Shopping Personality Test'} | 📊 GuiltGauge.com`;
    } else {
      // Dashboard emoji string
      return `${baseEmojis}${guiltEmoji} ${purchaseData.guiltScore}% | ${moodEmoji} | 💰 ${purchaseData.amount} | 📈 ${purchaseData.purchaseCount || '?'} | ${personalityEmoji}`;
    }
  };
  
  // Generate share text based on data type
  const getShareText = () => {
    if (cardType === "purchase") {
      return `I scored ${purchaseData.guiltScore}% guilt for my ${purchaseData.item} purchase on GuiltGauge.com!`;
    } else if (cardType === "quiz") {
      return `I took the ${purchaseData.quizTitle || "Shopping Personality Quiz"} and got "${purchaseData.result || userPersonality}"! Check yours at GuiltGauge.com`;
    } else {
      return `My shopping stats: ${purchaseData.guiltScore}% avg guilt, $${purchaseData.amount} spent across ${purchaseData.purchaseCount || '?'} purchases. Track yours at GuiltGauge.com`;
    }
  };
  
  // Get the emoji string
  const emojiString = generateEmojiString();
  
  // Prepare share data for social media links - AdBlock-friendly approach
  const shareText = getShareText();
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent('https://guiltgauge.com');
  
  // Social sharing options - purposely obfuscated for AdBlock evasion
  const shareOptions = [
    {
      id: "share-option-1", // Avoids using "twitter" in the ID
      iconSrc: "/images/X.svg", // SVG for X (Twitter)
      label: "Share",
      color: "bg-sky-400 hover:bg-sky-500",
      url: `https://t.co/share?text=${encodedText}&url=${encodedUrl}` // URL shortener for Twitter
    },
    {
      id: "share-option-2", // Avoids using "facebook" in the ID
      iconSrc: "/images/F.svg", // SVG for Facebook
      label: "Share",
      color: "bg-indigo-600 hover:bg-indigo-700",
      url: `https://www.sharing-site.com/sharer.php?u=${encodedUrl}&quote=${encodedText}` 
      // This won't actually work but will be replaced client-side
    },
    {
      id: "share-option-3", // Avoids using "linkedin" in the ID
      iconSrc: "/images/IN.svg", // SVG for LinkedIn
      label: "Share",
      color: "bg-blue-700 hover:bg-blue-800",
      url: `https://professional.network/sharing/share-offsite/?url=${encodedUrl}`
      // This won't actually work but will be replaced client-side
    },
    {
      id: "share-option-4", // Avoids using "whatsapp" in the ID
      iconSrc: "/images/W.svg", // SVG for WhatsApp
      label: "Share",
      color: "bg-green-500 hover:bg-green-600",
      url: `https://message.app/?text=${encodedText}%20${encodedUrl}`
      // This won't actually work but will be replaced client-side
    },
    {
      id: "share-option-5", // Avoids using "email" in the ID
      emoji: "✉️", // Keep envelope emoji for email
      label: "Share",
      color: "bg-gray-500 hover:bg-gray-600",
      url: `mailto:?subject=${encodeURIComponent('Check out my Guilt Gauge stats!')}&body=${encodedText}%20${encodedUrl}`
    }
  ];
  
  // Real URLs - stored separately to avoid detection
  // These will be assigned client-side
  const getRealUrl = (index) => {
    // Only expose the real URLs when used
    const realUrls = [
      `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      `mailto:?subject=${encodeURIComponent('Check out my Guilt Gauge stats!')}&body=${encodedText}%20${encodedUrl}`
    ];
    return realUrls[index];
  };
  
  // Handle share button click
  const handleShare = (e, index) => {
    e.preventDefault();
    
    // Get the real URL at click time to avoid detection
    const realUrl = getRealUrl(index);
    
    // Open in new window
    window.open(realUrl, '_blank', 'noopener,noreferrer');
  };
  
  // Handle copy to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(shareText + ' https://guiltgauge.com')
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => console.error('Failed to copy: ', err));
  };
  
  // Function to generate the image using html2canvas
  const generateImage = () => {
    if (!cardRef.current) return;
    
    setIsGeneratingImage(true);
    
    html2canvas(cardRef.current, {
      scale: 2, // Higher quality
      backgroundColor: null,
      logging: false,
    }).then(canvas => {
      // Convert canvas to data URL
      const imgUrl = canvas.toDataURL('image/png');
      setGeneratedImageUrl(imgUrl);
      
      // Create a download link
      const link = document.createElement('a');
      link.download = cardType === "purchase" 
        ? `guilt-gauge-purchase-${purchaseData.guiltScore}.png`
        : cardType === "quiz"
        ? `guilt-gauge-quiz-result.png`
        : "guilt-gauge-dashboard.png";
      link.href = imgUrl;
      link.click();
      
      // Update UI state
      setShareStep("generated");
      setIsGeneratingImage(false);
    }).catch(error => {
      console.error("Error generating image:", error);
      setIsGeneratingImage(false);
      // Still try to move to next step so user has options
      setShareStep("instructions");
    });
  };
  
  // Function to share via Web Share API if available
  const shareViaWebShare = async () => {
    if (!generatedImageUrl) return;
    
    try {
      // Create a blob from the data URL
      const blob = await (await fetch(generatedImageUrl)).blob();
      
      // Create a File object from the blob with appropriate name
      const fileName = cardType === "purchase" 
        ? `guilt-gauge-purchase-${purchaseData.guiltScore}.png`
        : cardType === "quiz"
        ? `guilt-gauge-quiz-result.png`
        : "guilt-gauge-dashboard.png";
      const file = new File([blob], fileName, { type: "image/png" });
      
      // Share data with the file
      const shareData = {
        title: cardType === "purchase" 
          ? "My Shopping Guilt Score" 
          : cardType === "quiz"
          ? `My ${purchaseData.quizTitle || "Shopping Quiz"} Result`
          : "My Shopping Dashboard",
        text: shareText,
        url: "https://guiltgauge.com",
        files: [file]
      };
      
      // Check if Web Share API supports sharing files
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback for browsers that don't support sharing files
        await navigator.share({
          title: cardType === "purchase" 
            ? "My Shopping Guilt Score" 
            : cardType === "quiz"
            ? `My ${purchaseData.quizTitle || "Shopping Quiz"} Result`
            : "My Shopping Dashboard",
          text: shareText,
          url: "https://guiltgauge.com"
        });
        
        // Show instructions since we couldn't share the image
        setShareStep("instructions");
      }
    } catch (error) {
      console.error("Error sharing via Web Share API:", error);
      // Show instructions on error
      setShareStep("instructions");
    }
  };
  
  // Reset the sharing process
  const resetShare = () => {
    setShareStep("initial");
    setGeneratedImageUrl("");
  };
  
  return (
    <div className="mt-6">
      <h3 className="font-semibold mb-3 dark:text-white">Share Your {cardType === "quiz" ? "Quiz Result" : "Shopping Guilt"}</h3>
      
      {/* Shareable Card Preview */}
      <div 
        ref={cardRef}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 mb-4 border-2 border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="text-2xl mr-2">
              {cardType === "quiz" ? "📝" : "🛍️"}
            </div>
            <div>
              <h1 className="text-lg font-bold dark:text-white">Guilt Gauge</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Shop first, ask questions later</p>
            </div>
          </div>
          
          {/* Different badge styles based on cardType */}
          {cardType === "purchase" ? (
            <div className={`text-xs px-2 py-1 rounded-full ${
              purchaseData.guiltScore < 30 ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400" :
              purchaseData.guiltScore < 70 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400" :
              "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400"
            }`}>
              Guilt Score: {purchaseData.guiltScore}%
            </div>
          ) : cardType === "quiz" ? (
            <div className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400 text-xs px-2 py-1 rounded-full">
              Quiz Result
            </div>
          ) : (
            <div className="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400 text-xs px-2 py-1 rounded-full">
              Shopping Dashboard
            </div>
          )}
        </div>
        
        {/* Different content based on cardType */}
        {cardType === "purchase" ? (
          /* Single Purchase Card Content */
          <div className="text-center mb-4">
            <div className="text-gray-600 dark:text-gray-300 font-medium">
              I just bought a <span className="font-bold">{purchaseData.item}</span> for ${purchaseData.amount}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Feeling: {purchaseData.mood} • Will I use it: {purchaseData.usageFrequency}
            </div>
          </div>
        ) : cardType === "quiz" ? (
          /* Quiz Results Card Content */
          <div className="text-center mb-4">
            <div className="text-gray-600 dark:text-gray-300 font-medium">
              My result: <span className="font-bold">{purchaseData.result || userPersonality || "Mindful Shopper"}</span>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {purchaseData.resultDescription || "Understanding your shopping habits is the first step to mindful spending."}
            </div>
          </div>
        ) : (
          /* Dashboard Card Content */
          <div className="mb-4">
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 text-center">
                <div className="text-xs text-gray-500 dark:text-gray-400">Total Spent</div>
                <div className="font-bold dark:text-white">${purchaseData.amount}</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 text-center">
                <div className="text-xs text-gray-500 dark:text-gray-400">Avg Guilt</div>
                <div className="font-bold dark:text-white">{purchaseData.guiltScore}%</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 text-center">
                <div className="text-xs text-gray-500 dark:text-gray-400">Common Mood</div>
                <div className="font-bold dark:text-white capitalize">{purchaseData.mood}</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 text-center">
                <div className="text-xs text-gray-500 dark:text-gray-400">Purchases</div>
                <div className="font-bold dark:text-white">{purchaseData.purchaseCount || '?'}</div>
              </div>
            </div>
          </div>
        )}
        
        {/* Shopping Personality - shown for both card types */}
        {userPersonality && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl p-3 mb-3">
            <div className="text-sm text-center">
              <span className="font-medium">My Shopping Personality:</span> {userPersonality}
            </div>
          </div>
        )}
        
        {/* Customized message based on cardType */}
        <div className="text-center text-xs text-gray-500 dark:text-gray-400">
          {cardType === "purchase" ? (
            purchaseData.guiltScore < 30
              ? "It's not a splurge if it sparks joy, right?"
              : purchaseData.guiltScore > 70
              ? "Oops. Still cute though."
              : "It's called retail therapy for a reason."
          ) : cardType === "quiz" ? (
            purchaseData.resultTagline || "Understanding leads to improvement!"
          ) : (
            "Track your shopping guilt and become a mindful spender!"
          )}
        </div>
        
        <div className="text-center text-xs mt-3 text-gray-400 dark:text-gray-500">
          Calculate your shopping guilt at GuiltGauge.com
        </div>
      </div>
      
      {/* Social Media Sharing Section - AdBlock-friendly implementation */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
        <h3 className="font-semibold mb-3 dark:text-white flex items-center">
          <span className="mr-2">🔗</span> Share Your Results
        </h3>
        
        {/* Preview of the share text */}
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg mb-4 text-sm border border-gray-200 dark:border-gray-600">
          {shareText}
        </div>
        
        {/* AdBlock-friendly share buttons */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          {shareOptions.map((option, index) => (
            <motion.a
              key={option.id}
              href={option.url}
              onClick={(e) => handleShare(e, index)}
              className={`w-[80px] flex flex-col items-center justify-center p-2 rounded-lg text-white ${option.color}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {option.iconSrc ? (
                <OptimizedImage 
                  src={option.iconSrc} 
                  alt="Share" 
                  className="w-6 h-6 mb-1 object-contain filter invert"
                  placeholder="image"
                  placeholderSrc="/images/placeholder.png"
                  fallbackSrc="/images/fallback.png"
                />
              ) : (
                <span className="text-xl mb-1">{option.emoji}</span>
              )}
              <span className="text-xs">{option.label}</span>
            </motion.a>
          ))}
          
          {/* Copy button - not usually blocked by AdBlock */}
          <motion.button
            onClick={handleCopy}
            className={`w-[80px] flex flex-col items-center justify-center p-2 rounded-lg text-white ${buttonStyles[theme]}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="text-xl mb-1">{copied ? '✅' : '📋'}</span>
            <span className="text-xs">{copied ? 'Copied!' : 'Copy'}</span>
          </motion.button>
        </div>
        
        <div className="text-xs text-center text-gray-500 dark:text-gray-400">
          {cardType === "purchase" 
            ? "Share your shopping guilt journey and help others become mindful spenders!"
            : cardType === "quiz" 
            ? "Share your quiz results and help friends discover their shopping habits too!"
            : "Share your shopping dashboard and inspire others to track their spending!"
          }
        </div>

        {/* Instagram tag suggestion for sharing */}
        <div className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
          Tag @guiltgauge when sharing your result on Instagram!
        </div>
      </div>
      
      {/* Share Buttons - Image Generation */}
      <motion.button
        onClick={generateImage}
        className={`w-full py-3 mt-4 rounded-xl text-white font-semibold ${buttonStyles[theme]} flex justify-center items-center`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        disabled={isGeneratingImage}
      >
        {isGeneratingImage ? (
          <>
            <span className="animate-spin mr-2">🔄</span> Generating Image...
          </>
        ) : (
          <>
            <span className="mr-2">📷</span> Generate Shareable Image
          </>
        )}
      </motion.button>
      
      {/* After Image Generation - Add option to share just emoji text */}
      {shareStep === "generated" && (
        <div className="space-y-4 mt-4">
          {/* Preview of generated image */}
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Your image has been generated and downloaded!</p>
            
            {generatedImageUrl && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-2 mb-3">
                <OptimizedImage
                  src={generatedImageUrl} 
                  alt="Generated Guilt Gauge" 
                  className="max-h-40 mx-auto"
                  placeholder="image"
                  placeholderSrc="/images/placeholder.png"
                  fallbackSrc="/images/fallback.png"
                />
              </div>
            )}
          </div>
          
          {/* Emoji only option */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-3 mb-3">
            <div className="text-sm text-center mb-2">
              <span className="font-medium dark:text-white">Quick Share with Emoji:</span>
            </div>
            <div className="bg-white dark:bg-gray-800 p-2 rounded text-sm border border-gray-200 dark:border-gray-700 mb-2">
              {emojiString} GuiltGauge.com
            </div>
            <div className="flex justify-center">
              <motion.button
                onClick={() => {
                  // Copy emoji string to clipboard
                  navigator.clipboard.writeText(`${emojiString} GuiltGauge.com`).then(
                    () => {
                      alert("Emoji text copied to clipboard!");
                    },
                    (err) => {
                      console.error("Could not copy text: ", err);
                    }
                  );
                }}
                className={`px-4 py-2 rounded-lg text-white text-sm ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Copy Emoji Text
              </motion.button>
            </div>
          </div>
          
          {/* Share options */}
          <div className="grid grid-cols-2 gap-3">
            {/* Web Share API option - if available */}
            {typeof navigator !== 'undefined' && navigator.share && (
              <motion.button
                onClick={shareViaWebShare}
                className={`col-span-2 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]} flex justify-center items-center`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="mr-2">📱</span> Share on Device
              </motion.button>
            )}
            
            <motion.button
              onClick={resetShare}
              className="py-2 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold flex justify-center items-center"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="mr-2">↺</span> Reset
            </motion.button>
          </div>
        </div>
      )}
      
      {/* Manual sharing instructions */}
      {shareStep === "instructions" && (
        <div className="space-y-4 mt-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 text-sm">
            <h4 className="font-semibold mb-2 flex items-center">
              <span className="text-lg mr-2">ℹ️</span> 
              Instructions to Share Your Image
            </h4>
            <ol className="space-y-2 list-decimal pl-5 text-gray-700 dark:text-gray-300">
              <li>The image has been downloaded to your device</li>
              <li>You can now attach this image to your social media post</li>
              <li>Copy and paste this text: <br/>
                <div className="bg-white dark:bg-gray-800 p-2 rounded mt-1 text-xs border border-gray-200 dark:border-gray-700">
                  {shareText}
                </div>
              </li>
            </ol>
          </div>
          
          <div className="flex space-x-3">
            <motion.button
              onClick={resetShare}
              className="flex-1 py-2 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Back
            </motion.button>
            
            <motion.button
              onClick={() => {
                // Generate a new image
                generateImage();
              }}
              className={`flex-1 py-2 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Generate Again
            </motion.button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareStatsCard;
